
import type {
	APIObjectType,
	GNCPeriod,
	GNCSocialInsuranceGroup,
	IAPIGrossNetCalculation
} from '@hokify/common';
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import HokInput from './HokInput.vue';
import Autocomplete from './Autocomplete.vue';
import HokSelect from './HokSelect.vue';
import HokSwitch from './HokSwitch.vue';
import CallToAction from './CallToAction.vue';
import ToolTip from './ToolTip.vue';
import ErrorBox from './ErrorBox.vue';
import { piniaAvailable } from '../helpers/piniaStoreAvailable';

function isHTMLElement(element: any): element is HTMLElement {
	return element && element instanceof HTMLElement;
}

export default defineComponent({
	name: 'GrossNetCalculator',
	components: {
		HokInput,
		Autocomplete,
		HokSwitch,
		CallToAction,
		ToolTip,
		ErrorBox,
		HokSelect
	},
	data() {
		const scroll = undefined as boolean | undefined;
		const distance = undefined as number | undefined;
		const daysId = { value: 'Keine' } as { id?: number; value: string };
		const exemptAmount = undefined as string | undefined;
		const nonCashRemuneration = undefined as string | undefined;
		const state = undefined as { id: number; value: string } | undefined;
		const remunerationError = undefined as boolean | undefined;
		const socialInsuranceGroup = 0 as GNCSocialInsuranceGroup;
		const gnc = undefined as {} | undefined;

		return {
			gnc,
			remunerationError,
			state,
			nonCashRemuneration,
			exemptAmount,
			distance,
			scroll,
			defaultGrossNet: 'Brutto',
			socialInsuranceGroup,
			grossNet: [
				{ type: false, value: 'Brutto' },
				{ type: true, value: 'Netto' }
			],
			isNet: false,
			currentJobField: '',
			year: new Date().getFullYear().toString(),
			states: [
				{ id: 0, value: 'Tirol' },
				{ id: 1, value: 'Burgenland' },
				{ id: 2, value: 'Kärnten' },
				{ id: 3, value: 'Niederösterreich' },
				{ id: 4, value: 'Oberösterreich' },
				{ id: 5, value: 'Salzburg' },
				{ id: 6, value: 'Steiermark' },
				{ id: 7, value: 'Wien' },
				{ id: 8, value: 'Vorarlberg' }
			],
			showMoreInformation: false,
			showIncomeTaxSocialInsurance: false,
			pensionFund: false,
			avab: false,
			familyBonusPlus: false,
			splitBonus: false,
			childCountBelow18: 'Keine',
			childCountAbove18: 'Keine',
			commuterInfo: false,
			daysId,
			commuterCategories: [
				{ id: 10, value: 'Keine' },
				{ id: 3, value: '1-3 Tage' },
				{ id: 2, value: '4-7 Tage' },
				{ id: 1, value: '8-10 Tage' },
				{ id: 0, value: '11 oder mehr' }
			],
			publicTransportAcceptable: false,
			piniaAvailable
		};
	},
	computed: {
		numberOfChildren(): { value: string }[] {
			return this.children();
		},
		internalRemuneration: {
			get(): number | undefined {
				return this.remuneration;
			},
			set(updatedRemuneration) {
				this.$emit('update-remuneration', Number(updatedRemuneration));
			}
		},
		internalPeriod: {
			get(): number | undefined {
				return this.period;
			},
			set(updatedPeriod) {
				this.$emit('update-period', Number(updatedPeriod));
			}
		},
		randomJobFields() {
			if (this.jobFields) {
				const myRandomFields = this.jobFields.slice(0);
				return myRandomFields.sort(() => Math.random() - 0.5);
			}
			return [];
		},
		selectedJobField(): string {
			if (this.randomJobFields?.length) {
				return this.randomJobFields[0].value;
			}
			return '';
		},
		showExploreJobsCTA(): boolean {
			if (this.isPwa) {
				return true;
			}
			return !this.$route.path.startsWith('/job');
		},
		gncResultsRef() {
			return this.$refs.gncResultsRef as HTMLElement;
		}
	},
	methods: {
		calcYears() {
			const max = new Date().getFullYear();
			const min = max - 5;
			const years: { value: string }[] = [];

			for (let i = max; i >= min; i -= 1) {
				const year = i.toString();
				years.push({ value: year });
			}
			return years;
		},
		children() {
			const children: { value: string }[] = [];

			for (let i = 0; i <= 10; i += 1) {
				if (i === 0) {
					children.push({ value: 'Keine' });
				} else {
					children.push({ value: i.toString() });
				}
			}
			return children;
		},
		setGrossNet(val) {
			this.isNet = val;
			this.calculate(false);
		},
		setYear(year) {
			this.year = year;
		},
		setState(state) {
			this.state = state;
		},
		setJobFields(jobField) {
			this.currentJobField = jobField.value;
		},
		async calculate(scroll) {
			this.scroll = scroll;
			let jobField;
			if (this.internalRemuneration && this.internalRemuneration > 0) {
				this.remunerationError = false;

				if (this.currentJobField) {
					jobField = this.currentJobField;
				} else if (this.selectedJobField) {
					jobField = this.selectedJobField;
				} else {
					jobField = '';
				}

				this.gnc = {
					socialInsuranceGroup: this.socialInsuranceGroup,
					remuneration: this.internalRemuneration,
					isNet: this.isNet,
					jobField,
					period: this.internalPeriod,
					year: this.year,
					state: this.state?.id,
					nonCashRemuneration: this.nonCashRemuneration,
					exemptAmount: this.exemptAmount,
					pensionFund: this.pensionFund,
					avab: this.avab,
					familyBonusPlus: {
						childCountBelow18:
							this.childCountBelow18 === 'Keine' ? 0 : Number(this.childCountBelow18),
						childCountAbove18:
							this.childCountAbove18 === 'Keine' ? 0 : Number(this.childCountAbove18),
						splitBonus: this.splitBonus
					},
					commuterInfo: {
						distance: this.distance || 0,
						daysId: this.daysId.value === 'Keine' ? 0 : this.daysId.id,
						publicTransportUnacceptable: !this.publicTransportAcceptable
					},
					jobId: this.jobId
				};
				this.$emit('send-gnc', this.gnc, this.scroll);
			} else {
				this.remunerationError = true;
			}
		},
		setPeriod(period) {
			this.internalPeriod = period;
			this.$nextTick(() => {
				this.calculate(false);
			});
		},
		onGncCalculationChanged() {
			if (this.scroll) {
				const element = this.gncResultsRef;
				if (isHTMLElement(element)) {
					element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			}
		},
		ontologySuggestion(term) {
			return piniaAvailable(this.$nuxt, '$valuesStore')
				? this.$nuxt.context.$valuesStore?.ontologySuggestion(term)
				: this.$store.dispatch('values/ontologySuggestion', term);
		},
		childPromise() {
			return this.numberOfChildren;
		},
		grossNetPromise() {
			return this.grossNet;
		},
		yearPromise() {
			return this.calcYears();
		},
		jobFieldsPromise() {
			return this.randomJobFields;
		},
		statePromise() {
			return this.states;
		},
		commuterPromise() {
			return this.commuterCategories;
		}
	},
	props: {
		gncCalculation: { type: Object as PropType<IAPIGrossNetCalculation>, required: false },
		isPwa: { type: Boolean, default: false },
		period: { type: Number as PropType<GNCPeriod>, default: 3 },
		remuneration: { type: Number },
		jobFields: { type: Array as PropType<{ value: string }[]>, required: false },
		jobId: { type: String as PropType<APIObjectType.Job>, required: false }
	},
	watch: {
		gncCalculation: [
			{
				handler: 'onGncCalculationChanged'
			}
		]
	}
});
